import { useState } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaMagic } from 'react-icons/fa';
import { FieldShell } from './FieldShell';
import { useFieldName, useDomFieldId } from './_useFieldName';

const BLOCK = 'LStudio__app_field';

/*
    🔥 CÓDIGO / JSON

    Textarea monoespaciado con validación en vivo y botón de formatear.
    Sirve para configuraciones, payloads de prueba y campos técnicos.

    Tab inserta una tabulación en vez de saltar de campo, que es lo
    que uno espera al escribir código.
*/

export const FieldCode = ({
    id, name, label, hint, error,
    value = '', onChange, onBlur = null, language = 'json',
    rows = 8, placeholder = '{\n  "clave": "valor"\n}',
    isDisabled = false, isReadOnly = false,
    span = 2, ...rest
}) => {

    const [useFormatError, setFormatError] = useState(null);

    const IS_JSON = language === 'json';

    const onValidate = () => {
        if (!IS_JSON || !value.trim()) return null;
        try { JSON.parse(value); return true; } catch { return false; }
    };

    const IS_VALID = onValidate();

    const onFormat = () => {
        try {
            const PRETTY = JSON.stringify(JSON.parse(value), null, 2);
            onChange?.({ target: { name: name ?? id, value: PRETTY } });
            setFormatError(null);
        } catch (err) {
            setFormatError('No se pudo formatear: el JSON tiene errores');
        }
    };

    const onKeyDown = (event) => {

        if (event.key !== 'Tab') return;

        event.preventDefault();

        const EL = event.target;
        const START = EL.selectionStart;
        const NEXT = `${value.slice(0, START)}  ${value.slice(EL.selectionEnd)}`;

        onChange?.({ target: { name: name ?? id, value: NEXT } });

        requestAnimationFrame(() => { EL.selectionStart = EL.selectionEnd = START + 2; });
    };

    const { domName, onChangeField, onBlurField } = useFieldName({ id, name, onChange, onBlur });

    const DOM_ID = useDomFieldId(id);

    return (
        <FieldShell id={DOM_ID} label={label} hint={hint} span={span} isDisabled={isDisabled}
            error={error ?? useFormatError} >

            <div className={`${BLOCK}__code`}>

                <div className={`${BLOCK}__code_head`}>

                    <span className={`${BLOCK}__code_lang`}>{language}</span>

                    {
                        IS_JSON && value.trim() && (
                            <span className={`${BLOCK}__code_status`} data-ok={IS_VALID}>
                                {IS_VALID ? <FaCheckCircle /> : <FaExclamationCircle />}
                                {IS_VALID ? 'válido' : 'con errores'}
                            </span>
                        )
                    }

                    {
                        IS_JSON && (
                            <button type="button" className={`${BLOCK}__code_button`}
                                disabled={isDisabled || !value.trim()} onClick={onFormat} >
                                <FaMagic /> Formatear
                            </button>
                        )
                    }

                </div>

                <textarea id={DOM_ID} data-field={name ?? id} name={domName} rows={rows}
                    className={`${BLOCK}__code_area`}
                    value={value} onChange={onChangeField} onBlur={onBlurField} onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    disabled={isDisabled} readOnly={isReadOnly}
                    spellCheck="false" autoComplete="off"
                    {...rest} />

            </div>

        </FieldShell>
    );
};
